import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames'
import { first } from 'lodash'
import { useTranslation } from '../i18n'
import { loadFacebookScript, facebookRegistration, setFacebookCookie } from '../utils/facebook'

import Button from '../components/Button'
import { AuthRegistrationType } from '../types/common'

interface Props {
  className?: string
  disabled?: boolean
  registrationType?: AuthRegistrationType
  onRequest?: (isOnRequest: boolean) => void
  onError?: (errorMessage: string) => void
  onRegistration?: (data: { result: string; message: string; next?: string }) => void
}

export default function FacebookLoginButton({
  className,
  disabled,
  registrationType = AuthRegistrationType.INHOUSE,
  onRequest,
  onError,
  onRegistration,
}: Props) {
  const { t } = useTranslation()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const onFacebookLogin = () => {
    if (!window.FB) {
      return onError(t('login.error.facebookConnectError'))
    }

    setIsLoading(true)
    onRequest(true)

    try {
      let nextPage = (Array.isArray(router.query.next) ? first(router.query.next) : router.query.next) ?? '/'

      FB.getLoginStatus(function(response) {
        if (response.status === 'not_authorized') {
          nextPage = '/includes15/welcome'
        }
      })

      FB.login(response => {
        if (response.status === 'connected') {
          facebookRegistration({
            registrationType,
            response,
            nextPage,
            onRegistration,
          })
        } else {
          onError(t('login.error.facebookConnectError'))
          onRequest(false)
          setIsLoading(false)
        }
        setFacebookCookie(response)
      })
    } catch (e) {
      onError(t('login.error.facebookConnectError'))
      setIsLoading(false)
      onRequest(false)
    }
  }

  useEffect(() => {
    loadFacebookScript(document, 'script', 'facebook-jssdk')
  }, [])

  return (
    <Button
      className={classNames('FacebookLoginButton', className)}
      title={t('login.facebook')}
      iconType="SocialFacebookCircle"
      color="blue"
      onClick={onFacebookLogin}
      isLoading={isLoading}
      disabled={disabled}
      fullWidth
    />
  )
}
